import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, MousePointer, Eye, TrendingUp, Plus, ExternalLink, CheckCircle, Clock, XCircle, AlertCircle, Send } from 'lucide-react';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function BusinessDashboard() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [revenue, setRevenue] = useState(null);
  const [ads, setAds] = useState([]);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verification, setVerification] = useState(null);
  const [verifyForm, setVerifyForm] = useState({ contact_email: '', website: '', description: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (!user.is_business) {
      navigate('/upgrade-business');
      return;
    }

    fetchData();
  }, [user, navigate]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        withCredentials: true
      };

      const [revenueRes, adsRes, linksRes, verifyRes] = await Promise.all([
        axios.get(`${API}/api/business/revenue`, config),
        axios.get(`${API}/api/business/ads`, config),
        axios.get(`${API}/api/business/links`, config),
        axios.get(`${API}/api/business/verification/status`, config),
      ]);

      setRevenue(revenueRes.data);
      setAds(adsRes.data);
      setLinks(linksRes.data);
      setVerification(verifyRes.data);
    } catch (error) {
      console.error('Error fetching business data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!verifyForm.contact_email || !verifyForm.contact_email.includes('@')) {
      toast.error('Valid contact email is required');
      return;
    }
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/business/verification/submit`, verifyForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Verification submitted');
      // Refresh status
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const res = await axios.get(`${API}/api/business/verification/status`, config);
      setVerification(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <nav className="border-b-4 border-black bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center">
              <Button
                onClick={() => navigate('/home')}
                variant="ghost"
                className="mr-4 border-2 border-black rounded-full"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Business Dashboard</h1>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Verification Status Section */}
        <div className="mb-8" data-testid="verification-section">
          {verification?.verification_status === 'verified' ? (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
              <div>
                <span className="text-sm font-semibold text-emerald-800">Verified Business</span>
                <span className="text-xs text-emerald-600 ml-2">since {verification.reviewed_at ? new Date(verification.reviewed_at).toLocaleDateString() : '—'}</span>
              </div>
            </div>
          ) : verification?.verification_status === 'pending' ? (
            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-4 flex items-center gap-3">
              <Clock className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <span className="text-sm font-semibold text-amber-800">Verification Pending</span>
                <p className="text-xs text-amber-600 mt-0.5">Your submission is being reviewed. This is a manual, human process — no automated decisions.</p>
              </div>
            </div>
          ) : verification?.verification_status === 'rejected' ? (
            <div className="bg-white border-2 border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="w-5 h-5 text-red-500" />
                <span className="text-sm font-semibold text-slate-800">Verification Not Approved</span>
              </div>
              {verification.rejection_note && (
                <p className="text-xs text-slate-500 mb-3">Reason: {verification.rejection_note}</p>
              )}
              <p className="text-xs text-slate-500 mb-4">You may resubmit after addressing any noted issues.</p>
              <form onSubmit={handleVerificationSubmit} className="space-y-3">
                <Input
                  type="email"
                  placeholder="Contact email"
                  required
                  value={verifyForm.contact_email}
                  onChange={(e) => setVerifyForm(f => ({ ...f, contact_email: e.target.value }))}
                  className="border-slate-300 text-sm"
                  data-testid="verify-email-input"
                />
                <Input
                  placeholder="Website (optional)"
                  value={verifyForm.website}
                  onChange={(e) => setVerifyForm(f => ({ ...f, website: e.target.value }))}
                  className="border-slate-300 text-sm"
                  data-testid="verify-website-input"
                />
                <textarea
                  placeholder="Brief description of your business (optional)"
                  value={verifyForm.description}
                  onChange={(e) => setVerifyForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="verify-description-input"
                />
                <Button type="submit" disabled={submitting} className="bg-slate-900 text-white text-sm rounded-lg" data-testid="verify-submit-btn">
                  <Send className="w-3 h-3 mr-1" /> Resubmit Verification
                </Button>
              </form>
            </div>
          ) : (
            /* unverified — show submission form */
            <div className="bg-white border-2 border-slate-200 rounded-xl p-5">
              <div className="flex items-center gap-2 mb-1">
                <AlertCircle className="w-5 h-5 text-slate-400" />
                <span className="text-sm font-semibold text-slate-800">Get Verified</span>
              </div>
              <p className="text-xs text-slate-500 mb-4">Verification is a trust signal — it tells the community your business is real and accountable. No algorithmic benefit, no ranking boost.</p>
              <form onSubmit={handleVerificationSubmit} className="space-y-3">
                <Input
                  type="email"
                  placeholder="Contact email *"
                  required
                  value={verifyForm.contact_email}
                  onChange={(e) => setVerifyForm(f => ({ ...f, contact_email: e.target.value }))}
                  className="border-slate-300 text-sm"
                  data-testid="verify-email-input"
                />
                <Input
                  placeholder="Website (optional)"
                  value={verifyForm.website}
                  onChange={(e) => setVerifyForm(f => ({ ...f, website: e.target.value }))}
                  className="border-slate-300 text-sm"
                  data-testid="verify-website-input"
                />
                <textarea
                  placeholder="Brief description of your business (optional)"
                  value={verifyForm.description}
                  onChange={(e) => setVerifyForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="verify-description-input"
                />
                <Button type="submit" disabled={submitting} className="bg-slate-900 text-white text-sm rounded-lg" data-testid="verify-submit-btn">
                  <Send className="w-3 h-3 mr-1" /> Submit for Verification
                </Button>
              </form>
            </div>
          )}
        </div>

        {/* Revenue Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-6 h-6 text-lime-500" />
              <span className="text-sm font-bold text-slate-600">Link Revenue</span>
            </div>
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>
              ${revenue?.link_revenue?.toFixed(2) || '0.00'}
            </div>
            <div className="text-sm text-slate-500 mt-1">{revenue?.link_clicks || 0} clicks</div>
          </div>

          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="w-6 h-6 text-pink-500" />
              <span className="text-sm font-bold text-slate-600">Ad Spent</span>
            </div>
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>
              ${revenue?.ad_spent?.toFixed(2) || '0.00'}
            </div>
            <div className="text-sm text-slate-500 mt-1">{revenue?.ad_clicks || 0} clicks</div>
          </div>

          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
            <div className="flex items-center gap-3 mb-2">
              <Eye className="w-6 h-6 text-cyan-500" />
              <span className="text-sm font-bold text-slate-600">Impressions</span>
            </div>
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>
              {revenue?.ad_impressions || 0}
            </div>
          </div>

          <div className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
            <div className="flex items-center gap-3 mb-2">
              <DollarSign className="w-6 h-6 text-indigo-500" />
              <span className="text-sm font-bold text-slate-600">Net Revenue</span>
            </div>
            <div className="text-3xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>
              ${revenue?.net_revenue?.toFixed(2) || '0.00'}
            </div>
          </div>
        </div>

        <Tabs defaultValue="ads" className="w-full">
          <TabsList className="border-2 border-black rounded-full p-1 bg-slate-100 mb-8">
            <TabsTrigger value="ads" className="rounded-full font-bold px-8">My Ads</TabsTrigger>
            <TabsTrigger value="links" className="rounded-full font-bold px-8">Tracked Links</TabsTrigger>
          </TabsList>

          <TabsContent value="ads">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Advertisements</h3>
              <Button
                onClick={() => navigate('/create-ad')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Ad
              </Button>
            </div>

            {ads.length === 0 ? (
              <div className="text-center py-16 border-4 border-dashed border-slate-300 rounded-3xl">
                <h4 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>No ads yet</h4>
                <p className="text-slate-600 mb-6">Create your first advertisement</p>
                <Button
                  onClick={() => navigate('/create-ad')}
                  className="bg-indigo-500 hover:bg-indigo-600 text-white border-2 border-black rounded-full font-bold shadow-brutal"
                >
                  Create Ad
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {ads.map(ad => (
                  <div key={ad.ad_id} className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
                    <div className="flex gap-6">
                      <img src={ad.image_url} alt={ad.title} className="w-32 h-32 object-cover rounded-xl border-2 border-black" />
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="text-xl font-bold text-slate-900" style={{fontFamily: 'Outfit'}}>{ad.title}</h4>
                            <p className="text-slate-600 text-sm">{ad.description}</p>
                          </div>
                          <Badge className={`${
                            ad.status === 'active' ? 'bg-lime-100' : 'bg-red-100'
                          } text-slate-900 border-2 border-black font-bold`}>
                            {ad.status}
                          </Badge>
                        </div>
                        <div className="grid grid-cols-4 gap-4 mt-4">
                          <div>
                            <div className="text-xs text-slate-600">Impressions</div>
                            <div className="text-lg font-bold">{ad.impressions}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-600">Clicks</div>
                            <div className="text-lg font-bold">{ad.clicks}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-600">Spent</div>
                            <div className="text-lg font-bold">${ad.spent.toFixed(2)}</div>
                          </div>
                          <div>
                            <div className="text-xs text-slate-600">Budget</div>
                            <div className="text-lg font-bold">${ad.budget.toFixed(2)}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="links">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>Tracked Links</h3>
            </div>

            {links.length === 0 ? (
              <div className="text-center py-16 border-4 border-dashed border-slate-300 rounded-3xl">
                <h4 className="text-xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>No tracked links yet</h4>
                <p className="text-slate-600">Add links to your posts to start earning per click</p>
              </div>
            ) : (
              <div className="space-y-4">
                {links.map(link => (
                  <div key={link.link_id} className="bg-white border-4 border-black rounded-2xl p-6 shadow-brutal">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <ExternalLink className="w-4 h-4" />
                          <a href={link.url} target="_blank" rel="noopener noreferrer" className="font-bold text-indigo-500 hover:underline">
                            {link.url}
                          </a>
                        </div>
                        <div className="text-sm text-slate-600">Post ID: {link.post_id}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>
                          ${link.total_revenue.toFixed(2)}
                        </div>
                        <div className="text-sm text-slate-600">{link.clicks} clicks × ${link.fee_per_click}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}